import {
  Box,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
} from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";

import { OrderDirection, InnerOrder, InputMaybe } from "~/graphql/__generated__/graphql";

const PAGE_SIZE = 50

type QVars<K extends string> = {
  offset: number
  orderBy: Record<K, InputMaybe<InnerOrder>>
}

export type Column<K extends string> = {
  label: string
  sort: K[] | false
  sx?: SxProps<Theme> | undefined
}

function LazyLoadTable<K extends string, QueryVariables extends QVars<K>, Item extends { id: number }>({
  columns,
  emptyLabel,
  items,
  loading,
  queryVariables,
  renderRow,
  setQueryVariables: _setQueryVariables,
} : {
  columns: Column<K>[]
  emptyLabel: ReactNode
  items: Item[] | undefined
  loading: boolean
  renderRow: (item: Item) => ReactNode
  queryVariables: QueryVariables
  setQueryVariables: (variables: QueryVariables) => void
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(-1);


  const setQueryVariables = (page: number, rowsPerPage: number, orderBy: QueryVariables["orderBy"]) => {
    const offset = Math.floor(page * rowsPerPage / PAGE_SIZE) * PAGE_SIZE
    _setQueryVariables({
      ...queryVariables,
      offset,
      orderBy,
    })
  }

  const visibleItems = useMemo(() => items ? items.slice((page * rowsPerPage) % PAGE_SIZE, ((page + 1) * rowsPerPage - 1) % PAGE_SIZE + 1) : [], [items, page, rowsPerPage])

  // Need to useEffect here instead of useMemo because the user may start paging back again
  // and this only knows what the total is once it reaches the end and if you page back
  // you're no longer at the end.
  useEffect(() => {
    if (items && items.length !== PAGE_SIZE) {
      const offset = Math.floor(page * rowsPerPage / PAGE_SIZE) * PAGE_SIZE
      setTotalCount(Math.max(totalCount, offset + items.length));
    } else if (items?.length === PAGE_SIZE && totalCount < PAGE_SIZE) {
      // If the count increased from 49 to 50 - eg, because an item was added - then this case resets the count so we're not stuck at 49 forever.
      setTotalCount(-1);
    }
  }, [items])

  return <Box>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map(({ label, sort, sx }) => <TableCell
              key={label}
              onClick={() => {
                if (!sort) { return }
                const direction = queryVariables.orderBy[sort[0]]?.direction === "asc" ? OrderDirection.Desc : OrderDirection.Asc;
                const orderBy = sort.reduce(
                  (orderBy, clause, idx) => {
                    // @ts-ignore - The type inference is a struggle, but this is correct.
                    orderBy[clause] = { direction, priority: idx };
                    return orderBy;
                  },
                  {} as QueryVariables["orderBy"]
                )
                setQueryVariables(page, rowsPerPage, orderBy);
              }}
              sx={sx}
            >
              {sort ? <TableSortLabel
                active={!!queryVariables.orderBy[sort[0]]}
                direction={queryVariables.orderBy[sort[0]]?.direction}
              >
                {label}
              </TableSortLabel> : label}
            </TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? <>
            <RowSkeleton cols={columns.length} />
            <RowSkeleton cols={columns.length} />
            <RowSkeleton cols={columns.length} />
          </> :
          visibleItems.length === 0 ?
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ textAlign: "center" }}>
                {emptyLabel}
              </TableCell>
            </TableRow>
          :
            visibleItems.map(renderRow)
          }
        </TableBody>
      </Table>
    </TableContainer>
    <TablePagination
      rowsPerPageOptions={[10, 25, PAGE_SIZE]}
      component="div"
      count={totalCount}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={(_, newPage) => { setPage(newPage); setQueryVariables(newPage, rowsPerPage, queryVariables.orderBy)}}
      onRowsPerPageChange={(e) => {
        setPage(0);
        setRowsPerPage(parseInt(e.target.value));
        setQueryVariables(0, parseInt(e.target.value), queryVariables.orderBy);
      }}
    />
  </Box>
}

export default LazyLoadTable;

const RowSkeleton = ({ cols }: { cols: number}) => (<TableRow>
  {new Array(cols).fill(null).map((_, i) => <TableCell key={i}><Skeleton width="100%" /></TableCell>)}
</TableRow>)
